'use client'

import Link from 'next/link'

import { useRouter } from 'next/navigation'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { isIOS } from 'react-device-detect'
import {
  FaApplePay,
  FaBitcoin,
  FaCcMastercard,
  FaCcVisa,
  FaChevronDown,
  FaChevronRight,
  FaChevronUp,
  FaCreditCard,
  FaEthereum,
  FaGooglePay,
  FaKey,
  FaMonero,
  FaPaypal,
  FaTelegram,
} from 'react-icons/fa'
import { ClipLoader } from 'react-spinners'
import Modal from '../../component/Modal'
import { CARD_BACKGROUNDS } from '../../constants/cardBackgrounds'
import { useAuth } from '../../contexts/AuthContext'
import {
  centroBanCountries,
  cryptoData,
  g2aBanCountries,
} from '../../helpers/config'
import Image from 'next/image'
import { checkServerStatus as askServerStatus } from '../../lib/server-status'
import { GEM_PACKS } from '../../lib/gem-packs'
import { withServerPrices } from '../../lib/gem-prices'
import useGemPrices from '../../hooks/useGemPrices'
import { buyGems } from '../../lib/api/billing.ts'
import GemPackSavings, { PlanOfferBadge } from '../../component/GemPackSavings'

const GetGems = () => {
  const gemPages = {
    900: 'https://www.g2a.com/promptchan-900-gems-key-global-i10000504567003',
    1800: 'https://www.g2a.com/promptchan-1800-gems-key-global-i10000504567004',
    3000: 'https://www.g2a.com/promptchan-3000-gems-key-global-i10000504567005',
    10000:
      'https://www.g2a.com/promptchan-10000-gems-key-global-i10000504567006',
  }

  const { t } = useTranslation()

  const {
    user,
    authResolved,
    subscription,
    disputeBanned,
    country,
    crUser,
    displayPlan,
    isPlusOrHigher,
  } = useAuth()
  const router = useRouter()
  // The backend's prices for this account (its half price, its set in the price test); the page's copy until then.
  const prices = useGemPrices(user, authResolved)
  const GEMS = withServerPrices(GEM_PACKS, prices)
  // v2's answer names its sale (`sale`, null when there is none): a sale is v2's to say, never the page's own flag.
  const v2Sale = prices && 'sale' in prices ? prices.sale : undefined

  const [modalOpen, setModalOpen] = useState(false)
  const [upgradeModalCryptoOpen, setUpgradeModalCryptoOpen] = useState(false)
  const [cryptoModalOpen, setCryptoModalOpen] = useState(false)
  const [g2aModalOpen, setG2aModalOpen] = useState(false)
  const [telegramModalOpen, setTelegramModalOpen] = useState(false)

  const [currentGemPack, setCurrentGemPack] = useState(null)
  const [currentGemPackCrypto, setCurrentGemPackCrypto] = useState(null)

  const [isQuestion1Open, setIsQuestion1Open] = useState(false)
  const [isQuestion5Open, setIsQuestion5Open] = useState(false)

  const [isLoading, setIsLoading] = useState(false)

  const [discount, setDiscount] = useState(false)
  const [blackFridayDiscount, setBlackFridayDiscount] = useState(false)

  const [discountLoading, setDiscountLoading] = useState(false)

  const [isMoreContentVisible, setIsMoreContentVisible] = useState(false)

  const [errorText] = useState('')
  const [errorModal, setErrorModal] = useState(false)

  const toggleMoreContent = () => {
    setIsMoreContentVisible(!isMoreContentVisible)
  }

  const [timeLeft, setTimeLeft] = useState('')

  useEffect(() => {
    if (!blackFridayDiscount) return
    const target = v2Sale
      ? Date.parse(v2Sale.until)
      : Number(process.env.NEXT_PUBLIC_BLACK_FRIDAY_TARGET)
    const timer = setInterval(() => {
      const diff = target - Date.now()
      if (diff <= 0) {
        setTimeLeft('00:00:00:00')
        clearInterval(timer)
        return
      }
      const d = Math.floor(diff / (1000 * 60 * 60 * 24))
      const h = Math.floor((diff / (1000 * 60 * 60)) % 24)
      const m = Math.floor((diff / (1000 * 60)) % 60)
      const s = Math.floor((diff / 1000) % 60)
      setTimeLeft(`${d}d ${h}h ${m}m ${s}s`)
    }, 1000)
    return () => clearInterval(timer)
  }, [blackFridayDiscount, v2Sale])

  useEffect(() => {
    if (disputeBanned) {
      router.push('/paymentdown')
    }
  }, [])

  useEffect(() => {
    const blackFriday =
      v2Sale === undefined
        ? (process.env.NEXT_PUBLIC_BLACK_FRIDAY ?? '') === 'true'
        : Boolean(v2Sale)
    if (user && subscription?.plan === 'Free') {
      setDiscount(false)
    } else {
      setDiscount(true)
      setBlackFridayDiscount(blackFriday)
    }
  }, [user, subscription, centroBanCountries, country, setDiscount, v2Sale])

  const checkServerStatus = useCallback(async () => {
    try {
      setDiscountLoading(true)
      if (user) {
        const token = await user.getIdToken()
        // Waits out a store switch (503 + Retry-After) and a 429; only a
        // persistent outage sends a buyer to /maintenance (S19).
        const status = await askServerStatus({
          url: `${process.env.NEXT_PUBLIC_PROMPTCHAN_CLOUD}/api/server-status`,
          token,
        })

        if (status.state === 'outage') {
          throw new Error('Server is down')
        }
      } else {
        console.error('User not authenticated')
      }
    } catch (error) {
      console.error(error)
      router.push('/maintenance')
    } finally {
      setDiscountLoading(false)
    }
  }, [user, router, setDiscount])

  useEffect(() => {
    // Gate on authResolved, not !loading: the auth bootstrap timeout can release `loading`
    // before onAuthStateChanged fires — don't bounce a slow-resolving signed-in user mid-purchase.
    if (authResolved && !user) {
      router.push('/signup')
    } else if (user) {
      // Only check payment-server status once a user exists — running it with a null
      // user just logged 'User not authenticated' noise on every mount.
      // router.push('/paymentdown')
      checkServerStatus()
    }
  }, [user, authResolved, router, checkServerStatus])

  const navigateG2a = async (gemId) => {
    if (typeof window === 'undefined') return

    try {
      const url = gemPages[gemId]
      if (!url) {
        console.log('Gem pack does not exist.')
        return
      }

      if (isIOS) {
        window.open(url, '_blank', 'noopener,noreferrer')
        return
      }

      window.open(url, '_blank', 'noopener,noreferrer')
    } catch (error) {
      console.log('Something went wrong.', error)
    }
  }

  const buyGemsCentrobill = async (gemType) => {
    if (typeof window === 'undefined') return

    setIsLoading(true)
    try {
      if (user) {
        const token = await user.getIdToken()

        // The price, half price included, is the server's: the request names the pack only.
        let response = await buyGems(token, gemType.gemId, 'card')

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const { gateway_url } = await response.json()

        window.location.href = gateway_url // Redirects in the same tab
      } else {
        console.error('User not authenticated')
      }
    } catch (error) {
      console.error('Error buying gems:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const buyGemsCrypto = async (gemType) => {
    if (typeof window === 'undefined') return

    // setErrorModal(true)
    // setErrorText(
    //   'Sorry, crypto is unavailable right now due to maintenence. Please use another payment method.'
    // )
    // return

    setIsLoading(true)
    try {
      if (user) {
        const token = await user.getIdToken()

        let response = await buyGems(token, gemType.gemId, 'crypto')

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const { checkout_url } = await response.json()

        window.location.href = checkout_url // Redirects in the same tab
      } else {
        console.error('User not authenticated')
      }
    } catch (error) {
      console.error('Error buying gems:', error)
    } finally {
      setIsLoading(false)
    }
  }

  if (discountLoading) {
    return (
      <>
        <title>Promptchan AI - Gems</title>
        <meta
          name="description"
          content="Enjoy more fast AI NSFW generations - Top Up on Gems."
        />
        <meta
          name="keywords"
          content="AI Porn, Image from text, NSFW AI, AI anime, AI hentai, Soulgen, Picso, AI image generator, NSFW, no restrictions, Midjourney, Stable Diffusion, Promptchan, Banned on Midjourney, Promptchan, Mage.space"
        />
        <p className="text-white text-center my-40 flex items-center justify-center">
          <ClipLoader color="#ffffff" size={24} />
        </p>
      </>
    )
  }

  return (
    <>
      <title>Promptchan AI - Gems</title>
      <meta
        name="description"
        content="Enjoy more AI NSFW generations - Top Up on Gems."
      />
      <meta
        name="keywords"
        content="AI Porn, Image from text, NSFW AI, AI anime, AI hentai, Soulgen, Picso, AI image generator, NSFW, no restrictions, Midjourney, Stable Diffusion, Promptchan, Banned on Midjourney, Promptchan, Mage.space"
      />

      <div
        className="min-h-screen relative overflow-hidden"
        style={{ backgroundColor: '#000' }}
      >
        <div className="pb-96 px-8">
          <Modal
            isOpen={modalOpen}
            closeModal={() => setModalOpen(false)}
            title="🚀 Unlock Your Potential"
            content={
              'Upgrade now to claim your Gems, experience faster speeds, and unlock advanced features. Make the most of your experience and take it to the next level!'
            }
            confirmation="Upgrade & Unlock Benefits"
            onConfirm={() => router.push('/upgrade')}
          />

          {/*
            Telegram tutorial — same shape as the G2A "Redeem Coupon Guide"
            above: a short numbered flow plus the screen the user will land on,
            because the purchase finishes in another app and people abandon a
            hand-off they cannot picture.
          */}
          <Modal
            isOpen={telegramModalOpen}
            closeModal={() => setTelegramModalOpen(false)}
            title={t('telegramGuideTitle')}
            content={
              <div>
                <p className="font-semibold text-white">
                  {t('telegramGuideStep1')}
                </p>
                <p>{t('telegramGuideStep2')}</p>
                <p>{t('telegramGuideStep3')}</p>
                <Image
                  src="/assets/telegram-upgrade.webp"
                  alt={t('telegramGuideTitle')}
                  width={320}
                  height={226}
                  className="rounded-lg mt-3 mx-auto w-[240px] max-w-full h-auto"
                  unoptimized
                />
              </div>
            }
            confirmation={t('openTelegram')}
            onConfirm={() => {
              window.open(
                'https://t.me/PromptchanOfficialBot',
                '_blank',
                'noopener,noreferrer',
              )
              setTelegramModalOpen(false)
            }}
          />

          <Modal
            isOpen={g2aModalOpen}
            closeModal={() => setG2aModalOpen(false)}
            title={`Redeem Coupon Guide`}
            content={
              <div>
                <p>1. Purchase a Gems coupon</p>
                <p>2. Copy the key</p>
                <p>
                  3. Go to Promptchan Settings, paste your key, and press
                  Redeem. Enjoy!
                </p>
              </div>
            }
            confirmation={`Buy Coupon`}
            onConfirm={() => navigateG2a(currentGemPack)}
            confirmation2={`Redeem Coupon`}
            onConfirm2={() => router.push('/settings')}
          />

          <Modal
            isOpen={upgradeModalCryptoOpen}
            closeModal={() => setUpgradeModalCryptoOpen(false)}
            title={`${t('waitYoure')} 🤨`}
            content={`${t('toGetThe')}`}
            confirmation={`${t('getGemsWith')}`}
            onConfirm={() => router.push('/upgrade')}
            confirmation2={`${t('justBuy')}`}
            onConfirm2={() => setCryptoModalOpen(true)}
          />

          <Modal
            isOpen={cryptoModalOpen}
            closeModal={() => setCryptoModalOpen(false)}
            title={t('bitcoinDisclaimer')}
            content={
              <div>
                <p className="font-bold mb-2">{t('bitcoinRefund')}</p>

                <p className="text-sm">{t('dueToTheHigh')}</p>
                <div className="overflow-x-auto mt-4 p-2">
                  {/* Add a wrapper div with max height and vertical overflow auto */}
                  <div className="max-h-[150px] overflow-y-auto ">
                    <table className="min-w-full bg-[var(--middle-bg-color)]">
                      <thead className="text-left bg-[var(--foreground-bg-color)] text-white">
                        <tr>
                          <th className="py-3 px-2 uppercase font-semibold text-sm">
                            Crypto
                          </th>
                          <th className="py-3 px-2 uppercase font-semibold text-sm">
                            Token
                          </th>
                          <th className="py-3 px-2 uppercase font-semibold text-sm">
                            Fee
                          </th>
                        </tr>
                      </thead>
                      <tbody className="text-gray-200">
                        {cryptoData.map((crypto, index) => (
                          <tr
                            key={index}
                            className="text-left border-b border-[var(--foreground-bg-color)] text-sm"
                          >
                            <td className="py-3 px-2 font-semibold">
                              {crypto.name}
                            </td>
                            <td className="py-3 px-2">{crypto.token}</td>
                            <td className="py-3 px-2">
                              {crypto.transactionFee}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            }
            confirmation="Buy with Crypto"
            onConfirm={() => buyGemsCrypto(currentGemPackCrypto)}
            confirmation2="Need Help?"
            onConfirm2={() =>
              router.push('/articles/how-to-buy-crypto-for-promptchan')
            }
          />

          <Modal
            isOpen={errorModal}
            closeModal={() => setErrorModal(false)}
            title="Error"
            content={errorText || 'Something went wrong.'}
            confirmation="Done"
            onConfirm={async () => {
              if (typeof window === 'undefined') return

              setErrorModal(false)
            }}
          />

          <div className="mx-auto mt-6">
            {/* Cross-link to plans, replacing the Upgrade/Gems segmented control
                that used to sit here (and on /upgrade). The toggle framed a
                subscription and a one-off top-up as equal-weight alternatives to
                everyone; this points each tier the way we actually want it to go,
                matching the header dropdown's existing rule.

                Promoted for Free visitors — a gems-only buyer is the case worth
                converting, so the upsell is the loudest thing in the header row.
                Quiet for subscribers, where /upgrade is a ladder step rather than
                a pitch. Hidden at 'Max', which has nothing above it.

                Studio/Pro keeps the quiet link even though Max is usually the only
                rung left: Max ships behind the upgrade page's own maxAbValue flag,
                which isn't fetched here, and a quiet link that occasionally leads
                to a ladder with nothing left to buy beats fetching a second flag
                to decide one link's visibility. */}

            <div className="mb-6 md:w-1/2 mx-auto">
              <h1
                className={` md:text-5xl text-4xl font-bold text-center mb-1 py-2 flex items-center justify-center`}
              >
                <span
                  style={{
                    ...(blackFridayDiscount
                      ? {
                          backgroundImage:
                            'linear-gradient(135deg, #eeeefe 0%, #c9c6f8 48%, #8f88f2 100%)',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          backgroundClip: 'text',
                          color: 'transparent',
                        }
                      : {
                          backgroundImage:
                            'linear-gradient(100deg, #c7d2fe, #818cf8)',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          backgroundClip: 'text',
                          color: 'transparent',
                        }),
                  }}
                  className="mr-2"
                >
                  {blackFridayDiscount
                    ? t('discountHeader')
                    : t('rechargeYour')}
                </span>
                {blackFridayDiscount && '☀️'}
              </h1>
              <h2 className="md:text-xl text-gray-300 text-center">
                {blackFridayDiscount ? t('discountSubHeader') : t('yourNext')}
              </h2>
            </div>

            {/*
              Non-subscribers get this CENTRED under the subheading, in the slot
              /upgrade uses for its monthly|annual switch — same vertical rhythm
              (mt-8), same rounded-full pill on --foreground-bg-color. It used to
              be a small right-aligned link in the header row, which reads as
              breadcrumb furniture rather than an offer.

              Subscribers keep the quiet inline link: for them /upgrade is a
              ladder step, not a pitch, so it stays out of the way.
            */}
            {displayPlan !== 'Max' &&
              (isPlusOrHigher ? (
                <div className="mt-4 flex justify-end">
                  <Link
                    href="/upgrade"

                    className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-400 transition-colors hover:text-white"
                  >
                    {t('upgrade')}
                    <FaChevronRight className="text-[10px]" />
                  </Link>
                </div>
              ) : (
                <div className="mt-8 flex flex-col items-center gap-2">
                  <Link
                    href="/upgrade"

                    className="inline-flex items-center gap-2 rounded-full border border-[#818cf8]/40 bg-[#818cf8]/10 px-6 py-2.5 text-sm font-semibold text-[#c7d2fe] shadow-sm transition-colors hover:bg-[#818cf8]/20 hover:text-white"
                  >
                    {t('upgrade')}
                    <FaChevronRight className="text-[10px]" />
                  </Link>
                </div>
              ))}

            {blackFridayDiscount && (
              <div className="mt-4 flex items-center justify-center">
                <div
                  className="inline-flex items-center gap-3 rounded-lg border border-white/10 bg-[var(--foreground-bg-color)] px-4 py-2 shadow-sm"
                  aria-live="polite"
                  title="Offer countdown"
                >
                  <span className="text-[11px] uppercase tracking-[0.2em] text-gray-400 select-none">
                    {t('expires')}
                  </span>
                  <span
                    className="text-md font-semibold text-white tracking-wider"
                    style={{ fontVariantNumeric: 'tabular-nums' }}
                  >
                    {timeLeft || '—'}
                  </span>
                </div>
              </div>
            )}

            <div className="mt-4 md:mt-16 max-w-5xl lg:flex flex flex-wrap justify-center align-center mx-auto">
              {GEMS.filter((gem) => !(crUser && !gem.crEnabled)).map((gem) => (
                <div
                  className={`w-80 p-8 m-2 sm:p-10 relative rounded-xl text-white flex flex-col items-center justify-center transition-transform duration-200 hover:scale-[1.01] group overflow-hidden`}
                  style={{
                    borderWidth: 1,
                    borderColor: '#343436',
                  }}
                  key={gem.id}
                >
                  {blackFridayDiscount ? (
                    <div
                      className="absolute top-7 right-0 transform translate-x-2/2 -translate-y-1/2 font-bold text-white py-2 px-3 rounded-full text-sm uppercase tracking-wide mr-2 z-20 shadow-sm"
                      style={{
                        borderWidth: 1,
                        borderColor: 'rgba(255,255,255,0.16)',
                        backgroundColor: 'var(--main-bg-color)',
                      }}
                    >
                      {`70% Off`}
                    </div>
                  ) : (
                    <PlanOfferBadge
                      halfPrice={prices ? prices.half_price : discount}
                      plan={subscription?.plan}
                    />
                  )}

                  <div
                    className="absolute inset-0 transition-opacity duration-300 ease-in-out group-hover:opacity-100 opacity-90"
                    style={{ backgroundImage: CARD_BACKGROUNDS.blue }}
                  ></div>

                  <div className="relative z-10 mt-4 w-full flex flex-col items-center justify-center">
                    <ul className="leading-6 text-white mx-auto">
                      <li className="flex items-center justify-center font-semibold text-xl">
                        <div className="mr-2">
                          <Image
                            priority
                            src="/assets/icons/gem.svg"
                            alt="Gem"
                            height={32}
                            width={32}
                          />
                        </div>
                        <div>
                          {gem.gems.toLocaleString('en-US')}{' '}
                          <span className="text-xs font-base">{t('gems')}</span>
                        </div>
                      </li>
                    </ul>
                    <p className="mt-4 mb-8 text-lg text-gray-300 text-center">
                      {(prices ? prices.half_price : discount) ? (
                        <>
                          <strike className="text-gray-400 font-light mr-1">
                            ${gem.price}
                          </strike>
                          {blackFridayDiscount ? (
                            <span className="text-white font-bold text-xl">
                              ${gem.blackFridayPrice}
                            </span>
                          ) : (
                            <span className="text-white font-bold text-xl">
                              ${gem.discountedPrice}
                            </span>
                          )}
                        </>
                      ) : (
                        <span className="text-gray-200 font-semibold text-xl">
                          ${gem.price}
                        </span>
                      )}
                    </p>
                    <GemPackSavings
                      gem={gem}
                      inTest={prices?.in_test && !blackFridayDiscount}
                      halfPrice={prices ? prices.half_price : discount}
                    />
                    {user ? (
                      <>
                        <div className="flex mt-auto items-center mx-auto mb-1">
                          <span className="text-gray-400 font-semibold text-xs ">
                            {t('debitOrCredit')}
                          </span>
                          <span className="text-gray-400 flex space-x-1 ml-1.5 text-xs">
                            <FaCcMastercard />
                            <FaCcVisa />
                          </span>
                        </div>
                        <button
                          className="h-12 px-2 rounded-xl text-center bg-[var(--main-bg-color)] w-full flex items-center justify-center my-1 mb-4 transition-transform duration-200 hover:scale-[1.02]"
                          data-track={`gems.card_${gem.gems}`}
                          onClick={() => {
                            setCurrentGemPack(gem)

                            if (!centroBanCountries?.includes(country)) {
                              buyGemsCentrobill(gem)
                            } else {
                              navigateG2a(gem.gems)
                            }
                          }}
                          disabled={isLoading}
                        >
                          {isLoading ? (
                            <ClipLoader color="#ffffff" size={14} />
                          ) : (
                            <p className="font-semibold text-white flex items-center justify-center">
                              <FaCreditCard className="mr-2" /> {t('buyGems')}
                            </p>
                          )}
                        </button>

                        <button
                          className="flex justify-center items-center text-sm p-4 text-gray-300"
                          onClick={toggleMoreContent}
                        >
                          More payment options{' '}
                          {isMoreContentVisible ? (
                            <FaChevronUp className="ml-2" />
                          ) : (
                            <FaChevronDown className="ml-2" />
                          )}
                        </button>

                        {isMoreContentVisible && (
                          <div className="mt-2 w-full flex flex-col justify-center items-center">
                            {/*
                              The dead "Key Unavailable" arm is gone (2026-09-18, user
                              decision) — no point rendering a button that cannot be
                              pressed. G2A now simply does not appear on a pack it cannot
                              sell (small/medium have no couponEnabled) or in a banned
                              country. The working keys are untouched.
                            */}

                            {/*
                              Telegram Stars — ABOVE crypto on purpose. Stars are
                              bought through Apple IAP on iOS and Google Play
                              billing on Android, so for the mobile majority this
                              is the most familiar rail here, not an exotic one.
                              (That ~30% store cut is also exactly why Stars nets
                              us less than a card — the same tax any mobile IAP
                              pays.)

                              Every pack EXCEPT `mythic`: GEM_PACKS in
                              myapp/telegram_payments.py carries
                              small/medium/large/mega/titan, and the 10,000-gem
                              pack has no Star price, so linking it would send
                              someone to a bot that cannot sell it.
                            */}
                            {/*
                              Unsupported shows DISABLED, not hidden — the same
                              precedent as "Key Unavailable" / "Crypto
                              Unavailable" above. A row that vanishes on one pack
                              and not the others reads as a rendering bug.
                            */}
                            {gem.gemId === 'mythic' ? (
                              <button
                                className="h-12 px-2 rounded-xl text-center bg-[var(--secondary-bg-color)] w-full flex items-center justify-center mb-4"
                                disabled={true}
                              >
                                <p className="font-semibold text-gray-400 flex items-center justify-center">
                                  <FaTelegram className="mr-2" />{' '}
                                  {t('telegramUnavailable')}
                                </p>
                              </button>
                            ) : (
                              <button
                                className="h-12 px-2 rounded-xl text-center bg-[var(--secondary-bg-color)] w-full flex items-center justify-center mb-4 transition-transform duration-200 hover:scale-[1.02]"
                                style={{
                                  borderWidth: 1,
                                  borderColor: '#2C2D30',
                                }}
                                onClick={() => setTelegramModalOpen(true)}
                              >
                                <p className="font-semibold text-white flex items-center justify-center">
                                  <FaTelegram className="mr-2" />{' '}
                                  {t('buyGemsTelegram')}
                                </p>
                              </button>
                            )}
                            {gem.gems < 250 ? (
                              <>
                                <button
                                  className="h-12 px-2 rounded-xl text-center bg-[var(--secondary-bg-color)] w-full flex items-center justify-center mb-4"
                                  disabled={true}
                                >
                                  <p className="font-semibold text-gray-400 flex items-center justify-center">
                                    <FaBitcoin className="mr-2" /> Crypto
                                    Unavailable
                                  </p>
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  className="h-12 px-2 rounded-xl text-center bg-[var(--secondary-bg-color)] w-full flex items-center justify-center transition-transform duration-200 hover:scale-[1.02]"
                                  data-track={`gems.crypto_${gem.gems}`}
                                  onClick={() => {
                                    setCurrentGemPackCrypto(gem)
                                    if (
                                      subscription?.plan === 'Free' ||
                                      subscription === null
                                    ) {
                                      setUpgradeModalCryptoOpen(true)
                                    } else {
                                      setCryptoModalOpen(true)
                                    }
                                  }}
                                  disabled={isLoading}
                                >
                                  {isLoading ? (
                                    <ClipLoader color="#ffffff" size={14} />
                                  ) : (
                                    <p className="font-semibold text-white flex items-center justify-center">
                                      <FaBitcoin className="mr-2" />{' '}
                                      {t('buyGemsCrypto')}
                                    </p>
                                  )}
                                </button>
                              </>
                            )}

                            {/*
                              Unavailable rather than absent, matching the Telegram row and
                              the "Crypto Unavailable" precedent: a rail that silently
                              vanishes on some packs reads as a rendering bug. G2A cannot
                              sell small/medium (no couponEnabled) or reach banned countries.
                            */}
                            {g2aBanCountries?.includes(country) ||
                            !gem.couponEnabled ? (
                              <button
                                className="h-12 px-2 rounded-xl text-center bg-[var(--secondary-bg-color)] w-full flex items-center justify-center mb-4"
                                disabled={true}
                              >
                                <p className="font-semibold text-gray-400 flex items-center justify-center">
                                  <FaKey className="mr-2" /> Key Unavailable
                                </p>
                              </button>
                            ) : (
                              <>
                                {/*
                                  No card icons here: G2A's checkout offers
                                  Paysafecard, Cashlib and G2A Balance, none of
                                  which is a card. The Debit/Credit row promised
                                  one this rail does not have.
                                */}
                                <button
                                  className="h-12 px-2 rounded-xl text-center bg-[var(--secondary-bg-color)] w-full flex items-center justify-center mb-4 transition-transform duration-200 hover:scale-[1.02]"
                                  style={{
                                    borderWidth: 1,
                                    borderColor: '#2C2D30',
                                  }}
                                  onClick={() => {
                                    // setErrorModal(true)
                                    // setErrorText(
                                    //   'Buy Key with Card is currently unavailable. Please use another payment method.',
                                    // )

                                    setG2aModalOpen(true)
                                    setCurrentGemPack(gem.gems)
                                  }}
                                >
                                  <p className="font-semibold text-white flex items-center justify-center">
                                    <FaKey className="mr-2" /> Buy Key with Card
                                  </p>
                                </button>
                              </>
                            )}
                          </div>
                        )}
                      </>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>

            <div className="px-6 lg:px-8 max-w-5xl mx-auto py-4 text-white mt-20">
              <div
                className="p-6 rounded-lg"
                style={{
                  backgroundColor: 'var(--middle-bg-color)',
                  opacity: 0.9, // Add background color
                }}
              >
                <h2 className="text-2xl font-semibold tracking-tight mb-4 text-center">
                  {t('faq')}
                </h2>
                <h4 className="text-center text-sm">
                  {t('cantFind')} {t('visitOur')}{' '}
                  <a
                    className="underline"
                    href="https://discord.gg/cZuJFFb34e"
                    target="_blank"
                    rel="noreferrer"
                  >
                    {t('discordCommunity')}
                  </a>{' '}
                  {t('toAskForHelp')}
                </h4>
                <div className="space-y-4 pt-6">
                  {/* . */}

                  <div className="border-b border-gray-800">
                    <button
                      onClick={() => setIsQuestion1Open(!isQuestion1Open)}
                      className="flex justify-between items-center w-full text-left text-xl font-semibold p-4"
                    >
                      <span>{t('whatAreGems')}</span>
                      <span>{isQuestion1Open ? '-' : '+'}</span>
                    </button>
                    <p
                      className={`pl-4 pr-10 pb-4 text-sm ${
                        isQuestion1Open ? 'block' : 'hidden'
                      }`}
                    >
                      {t('gemsAre2')}{' '}
                      <Link href="/upgrade" className="text-white underline">
                        Upgrade
                      </Link>{' '}
                      .
                    </p>
                  </div>
                  <div className="border-b border-gray-800">
                    <button
                      onClick={() => setIsQuestion5Open(!isQuestion5Open)}
                      className="flex justify-between items-center w-full text-left text-xl font-semibold p-4"
                    >
                      <span>{t('whatsTheDifference')}</span>
                      <span>{isQuestion5Open ? '-' : '+'}</span>
                    </button>
                    <p
                      className={`pl-4 pr-10 pb-4 text-sm ${
                        isQuestion5Open ? 'block' : 'hidden'
                      }`}
                    >
                      {t('theDifferenceIs')}{' '}
                      <Link href="/upgrade" className="underline">
                        Upgrade
                      </Link>
                    </p>
                  </div>
                </div>

                <p className="text-white text-sm mt-8 text-center">
                  {t('oneGemIs')}{' '}
                  <Link href="/upgrade" className="underline">
                    Upgrade
                  </Link>
                </p>
              </div>

              <p className="text-white text-sm mt-20 text-center">
                <span className="mt-4 ml-1">{t('irishAddress')}</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default GetGems
